import React from 'react';
import {
  LayoutGrid,
  Users,
  Clock,
  Wallet,
  MessageSquare,
  Share2,
  CheckCircle2,
  Layers,
} from 'lucide-react';

const HEADING = { fontFamily: "'Space Grotesk', system-ui, sans-serif" } as const;

const FEATURES = [
  {
    icon: LayoutGrid,
    title: 'Gestion de projet moderne',
    description:
      "Kanban, liste, calendrier, Gantt, jalons — choisissez la vue qui vous convient. Sans friction, sans limite.",
    highlights: ['Vues multiples', 'Jalons & sprints', 'Sous-tâches illimitées'],
    span: 'lg:col-span-8',
  },
  {
    icon: Users,
    title: 'Charge équipe',
    description: "Visualisez la charge, détectez les surcharges, répartissez intelligemment.",
    highlights: ['Temps réel', 'Multi-projets'],
    span: 'lg:col-span-4',
  },
  {
    icon: Clock,
    title: 'Feuilles de temps',
    description: 'Saisie rapide, validation manager, exports en un clic.',
    highlights: ['Workflow validation', 'Rapports détaillés'],
    span: 'lg:col-span-4',
  },
  {
    icon: Wallet,
    title: 'Budget projet',
    description:
      "Prévision vs réel en temps réel. Coûts main d'œuvre et dépenses externes centralisés — fini les tableurs.",
    highlights: ['Coûts temps réel', 'Alertes budget', 'Analyse rentabilité'],
    span: 'lg:col-span-8',
  },
  {
    icon: MessageSquare,
    title: 'Collaboration fluide',
    description: 'Commentaires, mentions, notifications smart — restez synchronisés.',
    highlights: ['@mentions', 'Threads', 'Notifications'],
    span: 'lg:col-span-6',
  },
  {
    icon: Share2,
    title: 'Partage & accès client',
    description: 'Liens sécurisés, portail client, contrôle granulaire de la visibilité.',
    highlights: ['Liens partage', 'Portail client', 'Rôles fins'],
    span: 'lg:col-span-6',
  },
];

export function FeaturesSection() {
  return (
    <section id="features" className="py-24 md:py-32 bg-[#0a0a1a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1e1e5a] text-[#a5b4fc] text-xs font-bold uppercase tracking-widest mb-6 border border-[#4f46e5]/30">
            <Layers className="h-3.5 w-3.5" />
            Fonctionnalités
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 tracking-tighter" style={HEADING}>
            Tout ce qu'il faut.{' '}
            <span className="text-slate-500">Rien de trop.</span>
          </h2>
          <p className="text-lg text-slate-400">
            Un workspace unifié pour piloter vos projets sans les usines à gaz.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className={`group relative ${feature.span} p-8 rounded-[2rem] bg-[#141432] border border-[#1e1e5a] hover:border-[#4f46e5]/50 transition-all duration-300 overflow-hidden`}
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#4f46e5]/0 group-hover:bg-[#4f46e5]/15 blur-[80px] rounded-full transition-all duration-500" />
                <div className="relative">
                  <div className="inline-flex p-3 rounded-2xl bg-[#1e1e5a] text-[#a5b4fc] mb-6 border border-[#4f46e5]/20">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" style={HEADING}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 mb-5 leading-relaxed">{feature.description}</p>
                  <ul className="flex flex-wrap gap-2">
                    {feature.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-center gap-1.5 text-xs font-medium text-slate-300 bg-[#1e1e5a]/60 border border-white/5 px-3 py-1.5 rounded-full"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 text-[#a5b4fc]" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
